// DeviceQuickManageModal.js
// Focused device-management panel — replaces "open the whole TV dashboard" with
// a small set of explicit functions: layouts (activate / rotation / transitions),
// playlists (activate + inline slide-time), schedules, file synchronization (WebDAV),
// and RSS messages.
//
// All HTTP goes through the MQTT-HTTP bridge. We use credentials:'omit' so the
// browser session cookie does not confuse the device's HTTP auth (the bridge
// injects Basic admin:admin when no Authorization header is present).

import React, { useEffect, useState, useCallback, useRef } from 'react';
import promediaService from '../services/promediaService';
import './DeviceQuickManageModal.css';

// Tabs have a short label for mobile (single emoji + 1 word) and a full label
// for tablet/desktop. The header still shows the full label inside the tab bar
// when there's room, but on phones we save space with the short label.
const TABS = [
  { id: 'layouts',   label: '🖥️ Layouts',                short: '🖥️' },
  { id: 'playlists', label: '🎬 Playlists & Slide Time', short: '🎬' },
  { id: 'schedule',  label: '📅 Schedules',              short: '📅' },
  { id: 'sync',      label: '🔄 File Sync',              short: '🔄' },
  { id: 'rss',       label: '📰 RSS',                    short: '📰' },
  { id: 'license',   label: '🔑 License',                short: '🔑' },
];

const DAYS = [
  { key: 'monday',    label: 'Mo' },
  { key: 'tuesday',   label: 'Tu' },
  { key: 'wednesday', label: 'We' },
  { key: 'thursday',  label: 'Th' },
  { key: 'friday',    label: 'Fr' },
  { key: 'saturday',  label: 'Sa' },
  { key: 'sunday',    label: 'Su' },
];

const TRANSITION_TYPES = [
  'NONE','CROSSFADE','CROSSFADE_TRUE','TO_RIGHT','TO_LEFT','TO_TOP','TO_BOTTOM',
  'SHRINK','ROTATE_SHRINK','ENLARGE_CROSSFADE','EXPLODE','FOLD',
  'HORIZONTAL_FLIP','VERTICAL_FLIP',
  'CUBE_INSIDE','CUBE_INSIDE_RANDOM','CUBE_OUTSIDE','CUBE_OUTSIDE_RANDOM',
  'HIDE','RANDOM'
];

function StatusBar({ status, busy }) {
  if (!status && !busy) return null;
  return (
    <div className={`dqm-status ${busy ? 'busy' : ''}`}>
      {busy ? '⏳ ' : ''}{status}
    </div>
  );
}

// ============================================================
// Layouts tab — activate, clear, rotation, transitions
// ============================================================
function LayoutsTab({ deviceName, onStatus }) {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    onStatus('Loading layouts…', true);
    try {
      const list = await promediaService.listLayouts(deviceName);
      setLayouts(list);
      onStatus(`Loaded ${list.length} layout(s)`, false);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setLoading(false); }
  }, [deviceName, onStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  const activate = async (l) => {
    onStatus(`Activating "${l.name}"…`, true);
    try {
      await promediaService.activateLayoutById(deviceName, l.id);
      onStatus(`✅ Activated "${l.name}"`, false);
      await refresh();
    } catch (e) { onStatus(`❌ ${e.message}`, false); }
  };

  const clearToScheduled = async () => {
    onStatus('Clearing to scheduled layout…', true);
    try {
      await promediaService.clearLayout();
      onStatus('✅ Back to scheduled layout', false);
      setTimeout(refresh, 600);
    } catch (e) { onStatus(`❌ ${e.message}`, false); }
  };

  const expand = async (l) => {
    if (expandedId === l.id) { setExpandedId(null); setEditData(null); return; }
    setExpandedId(l.id);
    setEditData(null);
    setEditLoading(true);
    onStatus(`Loading layout "${l.name}"…`, true);
    try {
      const data = await promediaService.getLayoutEdit(deviceName, l.id);
      setEditData(data);
      onStatus('', false);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
      setExpandedId(null);
    } finally { setEditLoading(false); }
  };

  const updatePanel = (idx, key, value) => {
    if (!editData) return;
    const panels = editData.panels.slice();
    const p = { ...panels[idx], properties: { ...(panels[idx].properties || {}) } };
    if (key === 'animationType') p.properties.ANIMATION_TYPE = value;
    if (key === 'animationLength') p.properties.ANIMATION_LENGTH = Number(value) || 0;
    if (key === 'name') p.name = value;
    panels[idx] = p;
    setEditData({ ...editData, panels });
  };

  const saveEdit = async () => {
    if (!editData || !expandedId) return;
    if (!editData.fields.layoutName || !editData.fields.layoutName.trim()) {
      onStatus('❌ Name cannot be empty', false);
      return;
    }
    setSaving(true);
    onStatus('Saving layout…', true);
    try {
      // saveLayoutEdit auto-reactivates the layout so transitions take effect
      // on screen immediately. Verifies persistence by reading back.
      await promediaService.saveLayoutEdit(deviceName, expandedId, editData.fields, editData.panels, { activate: true });
      onStatus('✅ Saved & applied on screen', false);
      setTimeout(refresh, 400);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setSaving(false); }
  };

  return (
    <div className="dqm-tab">
      <div className="dqm-toolbar">
        <button className="dqm-btn ghost" onClick={refresh} disabled={loading || saving}>↻ Refresh</button>
        <button className="dqm-btn warn" onClick={clearToScheduled} disabled={loading || saving}>↺ Clear to scheduled</button>
      </div>
      <table className="dqm-table">
        <thead><tr><th>Name</th><th>Status</th><th style={{ width: 260 }}>Actions</th></tr></thead>
        <tbody>
          {layouts.map(l => (
            <React.Fragment key={l.id}>
              <tr className={l.isActive ? 'active' : ''}>
                <td data-label="Name"><strong>{l.name}</strong></td>
                <td data-label="Status">{l.isActive ? <span className="badge active">Active</span> : <span className="badge">—</span>}</td>
                <td data-label="Actions">
                  {!l.isActive && (
                    <button className="dqm-btn primary small" onClick={() => activate(l)}>Activate</button>
                  )}
                  <button className="dqm-btn ghost small" onClick={() => expand(l)} style={{ marginLeft: 6 }}>
                    {expandedId === l.id ? '▲ Close' : '▼ Edit'}
                  </button>
                </td>
              </tr>
              {expandedId === l.id && (
                <tr className="dqm-expand-row">
                  <td colSpan={3}>
                    {editLoading && <div className="dqm-loading">Loading layout details…</div>}
                    {editData && (
                      <div className="dqm-edit-pane">
                        <div className="dqm-grid-2">
                          <div className="dqm-field">
                            <label>Name</label>
                            <input
                              type="text"
                              className="dqm-input"
                              value={editData.fields.layoutName || ''}
                              onChange={(e) => setEditData({ ...editData, fields: { ...editData.fields, layoutName: e.target.value } })}
                              disabled={saving}
                            />
                          </div>
                          <div className="dqm-field">
                            <label>Rotation</label>
                            <div className="dqm-rot-group" role="radiogroup" aria-label="Rotation">
                              {['0', '90', '180', '270'].map(deg => {
                                const active = String(editData.fields.rotation || '0') === deg;
                                return (
                                  <button
                                    key={deg}
                                    type="button"
                                    className={`dqm-rot-btn ${active ? 'on' : ''}`}
                                    onClick={() => setEditData({ ...editData, fields: { ...editData.fields, rotation: deg } })}
                                    disabled={saving}
                                    aria-pressed={active}
                                    title={`${deg}°`}
                                  >
                                    {/* Visual swap of 90↔270 so the icon's edge marker
                                        matches what users expect for each rotation value. */}
                                    <svg className="dqm-rot-icon" style={{ transform: `rotate(${deg === '90' ? 270 : deg === '270' ? 90 : Number(deg)}deg)` }} viewBox="0 0 40 32" width="38" height="30" aria-hidden="true">
                                      {/* TV body */}
                                      <rect x="2" y="3" width="36" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="2.5"/>
                                      {/* top edge marker */}
                                      <rect x="2" y="3" width="36" height="4" fill="currentColor"/>
                                      {/* stand */}
                                      <rect x="14" y="27" width="12" height="3" fill="currentColor"/>
                                    </svg>
                                    <span className="dqm-rot-label">{deg}°</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        <h4 className="dqm-h4">Zones (transitions)</h4>
                        <table className="dqm-table compact">
                          <thead>
                            <tr>
                              <th>Zone</th>
                              <th>Transition type</th>
                              <th>Length (ms)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {editData.panels.map((p, i) => (
                              <tr key={p.id ?? i}>
                                <td>
                                  <input
                                    type="text"
                                    className="dqm-input compact"
                                    value={p.name || ''}
                                    onChange={(e) => updatePanel(i, 'name', e.target.value)}
                                    disabled={saving}
                                  />
                                </td>
                                <td>
                                  <select
                                    className="dqm-select compact"
                                    value={p.properties?.ANIMATION_TYPE || 'NONE'}
                                    onChange={(e) => updatePanel(i, 'animationType', e.target.value)}
                                    disabled={saving}
                                  >
                                    {TRANSITION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min="0"
                                    max="9000"
                                    className="dqm-input compact"
                                    value={p.properties?.ANIMATION_LENGTH ?? 0}
                                    onChange={(e) => updatePanel(i, 'animationLength', e.target.value)}
                                    disabled={saving}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="dqm-actions">
                          <button className="dqm-btn primary" onClick={saveEdit} disabled={saving}>
                            {saving ? 'Saving…' : '💾 Save layout'}
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {layouts.length === 0 && !loading && (
            <tr><td colSpan="3" className="empty">No layouts found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Playlists tab — list + inline editable Slide Time + activate
// ============================================================
function PlaylistsTab({ deviceName, onStatus }) {
  const [playlists, setPlaylists] = useState([]);
  const [forms, setForms] = useState({}); // playlistId -> form data (with current duration)
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [dirty, setDirty] = useState({}); // playlistId -> bool

  const refresh = useCallback(async () => {
    setLoading(true);
    onStatus('Loading playlists…', true);
    try {
      const list = await promediaService.listPlaylists(deviceName);
      setPlaylists(list);
      // Load each playlist's form data in parallel (slide-time field).
      const entries = await Promise.all(list.map(async p => {
        try {
          const f = await promediaService.getPlaylistEdit(deviceName, p.id);
          return [p.id, f];
        } catch (e) {
          return [p.id, { duration: '?', _error: e.message }];
        }
      }));
      const next = {};
      entries.forEach(([id, f]) => { next[id] = f; });
      setForms(next);
      setDirty({});
      onStatus(`Loaded ${list.length} playlist(s)`, false);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setLoading(false); }
  }, [deviceName, onStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  const activate = async (p) => {
    onStatus(`Activating "${p.name}"…`, true);
    try {
      await promediaService.activatePlaylistById(deviceName, p.id);
      onStatus(`✅ Activated "${p.name}"`, false);
      await refresh();
    } catch (e) { onStatus(`❌ ${e.message}`, false); }
  };

  const updateField = (id, key, value) => {
    setForms({ ...forms, [id]: { ...(forms[id] || {}), [key]: value } });
    setDirty({ ...dirty, [id]: true });
  };

  const save = async (p) => {
    const form = forms[p.id];
    if (!form) return;
    if (!form.duration || Number(form.duration) < 1) {
      onStatus('❌ Duration must be at least 1 second', false);
      return;
    }
    setSavingId(p.id);
    onStatus(`Saving "${p.name}"…`, true);
    try {
      const after = await promediaService.savePlaylistEdit(deviceName, p.id, form);
      setForms({ ...forms, [p.id]: after });
      setDirty({ ...dirty, [p.id]: false });
      onStatus(`✅ "${p.name}" saved (slide ${after.duration}s, sound ${after.music || 'NONE'})`, false);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setSavingId(null); }
  };

  return (
    <div className="dqm-tab">
      <div className="dqm-toolbar">
        <button className="dqm-btn ghost xs" onClick={refresh} disabled={loading || savingId} title="Refresh">↻</button>
        <span className="dqm-hint" style={{ marginLeft: 'auto', fontSize: 11 }}>
          Slide time = seconds per image (videos ignore it). Sync code groups playlists.
        </span>
      </div>
      <table className="dqm-table dqm-table--playlists compact">
        <thead>
          <tr>
            <th>Name</th>
            <th style={{ width: 56 }} title="Slide time (seconds)">Time</th>
            <th style={{ width: 86 }}>Sound</th>
            <th style={{ width: 110 }} title="Sync code: playlists with the same code play in sync">Sync</th>
            <th style={{ width: 58 }}>Status</th>
            <th style={{ width: 110 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {playlists.map(p => {
            const f = forms[p.id] || {};
            const isSaving = savingId === p.id;
            const isDirty = !!dirty[p.id];
            return (
              <tr key={p.id} className={p.isActive ? 'active' : ''}>
                <td data-label="Name" title={p.name}><strong className="dqm-pl-name">{p.name}</strong></td>
                <td data-label="Time">
                  <input
                    type="number"
                    min="1"
                    className="dqm-input dqm-input--xs"
                    value={f.duration ?? ''}
                    onChange={(e) => updateField(p.id, 'duration', e.target.value)}
                    disabled={isSaving || loading}
                  />
                </td>
                <td data-label="Sound">
                  <select
                    className="dqm-select dqm-input--xs"
                    value={f.music || 'NONE'}
                    onChange={(e) => updateField(p.id, 'music', e.target.value)}
                    disabled={isSaving || loading}
                    title={f.music === 'VIDEO' ? 'Sound: from video' : 'Sound: muted'}
                  >
                    <option value="NONE">Mute</option>
                    <option value="VIDEO">Video</option>
                  </select>
                </td>
                <td data-label="Sync">
                  <input
                    type="text"
                    maxLength={100}
                    pattern="[A-Za-z0-9 ]*"
                    className="dqm-input dqm-input--xs"
                    placeholder="—"
                    value={f.sync_code ?? ''}
                    onChange={(e) => updateField(p.id, 'sync_code', e.target.value)}
                    disabled={isSaving || loading}
                    title="A-Z 0-9 spaces — playlists with the same code play in sync"
                  />
                </td>
                <td data-label="Status">{p.isActive ? <span className="badge active">●</span> : <span className="badge">○</span>}</td>
                <td data-label="Actions" className="dqm-pl-actions">
                  <button
                    className={`dqm-btn ${isDirty ? 'primary' : 'ghost'} xs`}
                    onClick={() => save(p)}
                    disabled={isSaving || loading || !isDirty}
                    title="Save changes"
                  >{isSaving ? '…' : '💾'}</button>
                  {!p.isActive && (
                    <button
                      className="dqm-btn primary xs"
                      onClick={() => activate(p)}
                      disabled={isSaving || loading}
                      title="Activate this playlist"
                    >▶</button>
                  )}
                </td>
              </tr>
            );
          })}
          {playlists.length === 0 && !loading && (
            <tr><td colSpan="6" className="empty">No playlists found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Schedule tab — re-styled (wider selects, compact DOW grid)
// ============================================================
function ScheduleTab({ deviceName, onStatus }) {
  const [mode, setMode] = useState('layout');
  const [panels, setPanels] = useState([]);
  const [panelId, setPanelId] = useState('');
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const list = await promediaService.listPanels(deviceName);
      const main = list.filter(p => p.id !== 'audio');
      setPanels(main);
      if (main.length > 0 && !panelId) setPanelId(main[0].id);
    })();
    // eslint-disable-next-line
  }, [deviceName]);

  const load = useCallback(async () => {
    setLoading(true);
    onStatus(`Loading ${mode === 'layout' ? 'layout' : 'panel'} schedule…`, true);
    try {
      const result = mode === 'layout'
        ? await promediaService.getScreenLayoutSchedule(deviceName)
        : await promediaService.getPanelSchedule(deviceName, panelId);
      setRows(result.rows || []);
      setOptions(result.options || []);
      onStatus(`Loaded ${result.rows?.length || 0} row(s)`, false);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setLoading(false); }
  }, [deviceName, mode, panelId, onStatus]);

  useEffect(() => {
    if (mode === 'panel' && !panelId) return;
    load();
  }, [load, mode, panelId]);

  const update = (i, key, value) => {
    const next = rows.slice();
    next[i] = { ...next[i], [key]: value };
    setRows(next);
  };
  const addRow = () => {
    setRows([...rows, {
      id: '', ref: options[0]?.id || '',
      timeFrom: '00:00', timeTo: '23:59',
      dateFrom: '', dateTo: '',
      priority: '3',
      monday: true, tuesday: true, wednesday: true, thursday: true,
      friday: true, saturday: true, sunday: true
    }]);
  };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    onStatus('Saving schedule…', true);
    try {
      if (mode === 'layout') {
        await promediaService.saveScreenLayoutSchedule(deviceName, rows);
      } else {
        await promediaService.savePanelSchedule(deviceName, panelId, rows);
      }
      onStatus('✅ Saved', false);
      setTimeout(load, 400);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setSaving(false); }
  };

  return (
    <div className="dqm-tab">
      <div className="dqm-toolbar wrap">
        <div className="dqm-segment">
          <button
            className={`dqm-seg-btn ${mode === 'layout' ? 'active' : ''}`}
            onClick={() => setMode('layout')}
            disabled={loading || saving}
          >Screen layout schedule</button>
          <button
            className={`dqm-seg-btn ${mode === 'panel' ? 'active' : ''}`}
            onClick={() => setMode('panel')}
            disabled={loading || saving || panels.length === 0}
          >Panel (playlist) schedule</button>
        </div>
        {mode === 'panel' && (
          <select
            className="dqm-select"
            value={panelId}
            onChange={(e) => setPanelId(e.target.value)}
            disabled={loading || saving}
            style={{ minWidth: 180 }}
          >
            {panels.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <button className="dqm-btn ghost" onClick={load} disabled={loading || saving}>↻ Reload</button>
      </div>

      <div className="dqm-schedule-wrap">
        <table className="dqm-sched-table">
          <colgroup>
            <col className="col-layout" />
            <col className="col-time" />
            <col className="col-time" />
            <col className="col-dow" />
            <col className="col-date" />
            <col className="col-date" />
            <col className="col-prio" />
            <col className="col-rm" />
          </colgroup>
          <thead>
            <tr>
              <th>{mode === 'layout' ? 'Layout' : 'Playlist'}</th>
              <th>From</th><th>To</th>
              <th>Days of week</th>
              <th>Date from</th><th>Date to</th>
              <th>Priority</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td data-label={mode === 'layout' ? 'Layout' : 'Playlist'}>
                  <select
                    className="dqm-select"
                    value={r.ref}
                    onChange={(e) => update(i, 'ref', e.target.value)}
                  >
                    {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </td>
                <td data-label="From"><input type="time" className="dqm-input" value={r.timeFrom} onChange={(e) => update(i, 'timeFrom', e.target.value)} /></td>
                <td data-label="To"><input type="time" className="dqm-input" value={r.timeTo} onChange={(e) => update(i, 'timeTo', e.target.value)} /></td>
                <td data-label="Days">
                  <div className="dqm-dow">
                    {DAYS.map(d => (
                      <label key={d.key} className={`dqm-dow-pill ${r[d.key] ? 'on' : ''}`} title={d.key}>
                        <input
                          type="checkbox"
                          checked={!!r[d.key]}
                          onChange={(e) => update(i, d.key, e.target.checked)}
                        />
                        <span>{d.label}</span>
                      </label>
                    ))}
                  </div>
                </td>
                <td data-label="Date from"><input type="date" className="dqm-input" value={r.dateFrom} onChange={(e) => update(i, 'dateFrom', e.target.value)} /></td>
                <td data-label="Date to"><input type="date" className="dqm-input" value={r.dateTo} onChange={(e) => update(i, 'dateTo', e.target.value)} /></td>
                <td data-label="Priority">
                  <select className="dqm-select" value={r.priority} onChange={(e) => update(i, 'priority', e.target.value)}>
                    <option value="2">Low</option>
                    <option value="3">Medium</option>
                    <option value="4">High</option>
                  </select>
                </td>
                <td data-label="Actions" className="dqm-sched-rm">
                  <button className="dqm-btn danger small" onClick={() => removeRow(i)} title="Remove row">× Remove</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} className="empty">No schedule rows. Click "+ Add row" below.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="dqm-actions">
        <button className="dqm-btn ghost" onClick={addRow} disabled={loading || saving || options.length === 0}>+ Add row</button>
        <button className="dqm-btn primary" onClick={save} disabled={loading || saving}>
          {saving ? 'Saving…' : '💾 Save'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// File Sync tab — WebDAV view/edit/sync-now (with high-emphasis confirm)
// ============================================================
function FileSyncTab({ device, deviceName, onStatus, onClose, onDeviceChanged }) {
  // 2026-05-19 — identity editor at the top of File Sync. Admin can change
  // the device's friendly DISPLAY NAME (UserSettings.DEVICE_NAME) and its
  // MQTT TOPIC (UserSettings.DEVICE_TOKEN = device_uid). Both rebuilds push
  // the new setting to the agent and trigger a reload so it takes effect on
  // the next connection. Changing the MQTT token is the manual remedy for
  // UID collisions: rotate the offending device to a new topic, it appears
  // in No Owner with the new UID, and admin adopts it.
  const [editName, setEditName] = useState(device?.device_name || '');
  const [editToken, setEditToken] = useState(device?.device_uid || '');
  const [savingName, setSavingName] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  useEffect(() => {
    setEditName(device?.device_name || '');
    setEditToken(device?.device_uid || '');
  }, [device?.device_name, device?.device_uid]);

  // 2026-05-20 — All confirm() prompts in File Sync removed per admin
  // request ("admin knows what he does"). saveDisplayName / saveMqttToken /
  // save / syncNow / del all proceed without a prompt now.
  const saveDisplayName = async () => {
    const trimmed = (editName || '').trim();
    if (!trimmed) { onStatus('❌ Display name cannot be empty', false); return; }
    if (trimmed === (device?.device_name || '')) return;
    setSavingName(true);
    onStatus('Renaming device…', true);
    try {
      const token = localStorage.getItem('token');
      const oldUid = device?.device_uid || deviceName;
      const r = await fetch(`/api/devices/${encodeURIComponent(oldUid)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          device_name:      trimmed,
          description:      device?.description ?? '',
          register_date:    device?.register_date ?? null,
          anydesk_id:       device?.anydesk_id ?? '',
          anydesk_password: device?.anydesk_password ?? '',
          media_files_link: device?.media_files_link ?? '',
          brand_id:         device?.brand_id ?? null,
          branch_id:        device?.branch_id ?? null,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { onStatus(`❌ Rename failed: ${body.error || r.status}`, false); return; }
      // Push to agent so its local Web UI shows the new label, then reload.
      try {
        promediaService.setDevice(oldUid);
        await promediaService.setSettingsValue(oldUid, 'device_name', trimmed);
        promediaService.reloadApp().catch(() => {});
      } catch (e) { /* best-effort */ }
      onStatus('✅ Display name updated', false);
      // 2026-05-20 — Bubble the change so the dashboard list, Edit Player
      // modal, etc. all re-read the row instead of showing stale name.
      try { onDeviceChanged?.({ device_uid: oldUid, device_name: trimmed }); } catch (_) {}
    } catch (e) {
      onStatus(`❌ Rename failed: ${e.message}`, false);
    } finally { setSavingName(false); }
  };

  const saveMqttToken = async () => {
    const newTok = (editToken || '').trim();
    const oldTok = device?.device_uid || deviceName;
    if (!newTok)              { onStatus('❌ MQTT topic cannot be empty', false); return; }
    if (newTok === oldTok)    return;
    if (!/^[A-Za-z0-9._-]+$/.test(newTok)) {
      onStatus('❌ MQTT topic: letters, digits, dot, underscore, hyphen only', false);
      return;
    }
    setSavingToken(true);
    onStatus('Changing MQTT topic…', true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`/api/devices/${encodeURIComponent(oldTok)}/change-mqtt-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ new_token: newTok }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { onStatus(`❌ Token change failed: ${body.error || r.status}`, false); return; }
      // Push setSettingsValue + reload to the OLD topic — the agent is still
      // listening there. After reload it'll re-register on the new topic.
      try {
        promediaService.setDevice(oldTok);
        await promediaService.setSettingsValue(oldTok, 'device_token', newTok);
        promediaService.reloadApp().catch(() => {});
      } catch (e) { /* best-effort */ }
      onStatus('✅ MQTT topic updated — device will reconnect on the new topic shortly', false);
      try { onDeviceChanged?.({ device_uid: newTok, prev_device_uid: oldTok }); } catch (_) {}
      // The device row moved to a new UID; current modal references the old
      // identifier and would 404 on subsequent ops, so close it.
      setTimeout(() => { try { onClose?.(); } catch {} }, 1500);
    } catch (e) {
      onStatus(`❌ Token change failed: ${e.message}`, false);
    } finally { setSavingToken(false); }
  };

  const nameDirty  = (editName  || '').trim() !== (device?.device_name || '');
  const tokenDirty = (editToken || '').trim() !== (device?.device_uid  || '');
  // SINGLE-SYNC model: the UI shows at most one sync, treats the first one on
  // the device as "the current sync" and ignores any extras (legacy devices may
  // have multiple entries). Add ↔ Edit ↔ Delete all act on that one slot.
  const [current, setCurrent] = useState(null); // { id, from, to, clear } from listing
  const [extraCount, setExtraCount] = useState(0); // > 0 if device has more than one (legacy)
  const [wd, setWd] = useState(null); // edit-form state (only populated while editing/adding)
  const [wdBaseline, setWdBaseline] = useState(null); // last "saved" snapshot, used for dirty-check
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [intervalVal, setIntervalVal] = useState('0');
  const [intervalDirty, setIntervalDirty] = useState(false);
  const [savingInterval, setSavingInterval] = useState(false);

  const [progress, setProgress] = useState(null); // { state, startedAt, message }
  const [, setTick] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!progress || progress.state !== 'running') return;
    const h = window.setInterval(() => setTick(t => t + 1), 1000);
    return () => window.clearInterval(h);
  }, [progress]);

  const refresh = useCallback(async () => {
    setLoading(true);
    onStatus('Loading file synchronization…', true);
    try {
      const [items, iv] = await Promise.all([
        promediaService.listGrabbers(deviceName),
        promediaService.getGrabberInterval(deviceName).catch(() => '0')
      ]);
      const first = items[0] || null;
      setCurrent(first);
      setExtraCount(Math.max(0, items.length - 1));
      setIntervalVal(iv || '0');
      setIntervalDirty(false);
      // 2026-05-19 — Always inline-edit. As soon as there's a sync, populate
      // `wd` from the listing data so the editable form shows immediately.
      // 2026-05-20 — Pre-fill username from the device's owner_username so
      // admins don't see an empty/wrong user while credentials lazy-load.
      // Once getGrabberWebdav returns, its values override (they're the
      // authoritative copy actually saved on the agent).
      if (first) {
        const ownerFallback = (device?.owner_username || '').trim();
        const initial = {
          id: first.id,
          url: stripWebdavPrefix(first.from) || '',
          username: ownerFallback,
          password: '',
          folder: first.to || '/',
          clearFolder: (first.clear || '').toLowerCase().includes('yes'),
        };
        setWd(initial);
        setWdBaseline(initial);
        setEditing(true);
        // Lazy-load credentials from the device. The agent's values are
        // authoritative — if it returns a username/password, replace the
        // owner-fallback prefill. If the user already started typing,
        // don't clobber their edits.
        promediaService.getGrabberWebdav(deviceName, first.id).then(fromDevice => {
          if (!fromDevice) return;
          setWd(prev => {
            if (!prev || prev.id !== first.id) return prev;
            // Treat the owner-fallback as "not user-typed" so the agent's
            // real value wins. Real edits (anything other than the fallback)
            // are preserved.
            const userIsFallback = prev.username === ownerFallback;
            const passIsBlank    = !prev.password;
            return {
              ...prev,
              username: userIsFallback ? (fromDevice.username || prev.username) : prev.username,
              password: passIsBlank    ? (fromDevice.password || prev.password) : prev.password,
              folder:   prev.folder    || fromDevice.folder    || '/',
            };
          });
          setWdBaseline(prev => prev && prev.id === first.id ? {
            ...prev,
            username: fromDevice.username || prev.username,
            password: fromDevice.password || prev.password,
            folder:   fromDevice.folder   || prev.folder,
          } : prev);
        }).catch(() => { /* non-fatal — owner-fallback already populated */ });
      } else {
        setWd(null);
        setWdBaseline(null);
        setEditing(false);
      }
      const intLabel = (iv === '0' || !iv) ? 'off' : iv + 's';
      const tag = items.length === 0 ? 'no sync configured'
                : items.length === 1 ? '1 sync'
                : `${items.length} syncs found (only first shown)`;
      onStatus(`${tag} — auto interval ${intLabel}`, false);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setLoading(false); }
  }, [deviceName, onStatus]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const saveInterval = async () => {
    const n = Number(intervalVal);
    if (Number.isNaN(n) || n < 0) {
      onStatus('❌ Interval must be 0 or a positive number of seconds', false);
      return;
    }
    if (n > 0 && n < 60) {
      if (!window.confirm(`⚠ Interval ${n}s is less than 60s and may stress the device. Continue?`)) return;
    }
    setSavingInterval(true);
    onStatus('Saving sync interval…', true);
    try {
      await promediaService.setGrabberInterval(deviceName, n);
      const back = await promediaService.getGrabberInterval(deviceName);
      setIntervalVal(back || '0');
      setIntervalDirty(false);
      onStatus(`✅ Auto-sync interval saved (${(back === '0' || !back) ? 'off' : back + 's'})`, false);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setSavingInterval(false); }
  };

  // Watch device history for a NEW entry matching the source URL.
  // CRITICAL: baselineCount must be captured BEFORE the sync POST. Otherwise a
  // fast sync (small/cached files complete in <1s) can append its history entry
  // before we read the baseline, so the "new count > baseline" check never trips
  // and we sit polling until the 5-minute timeout. Even on the user's working
  // syncs this was the failure mode.
  const startProgressWatch = (g, baselineCount, baselineLatestTs) => {
    const startedAt = Date.now();
    setProgress({ state: 'running', startedAt, message: 'Sync in progress…' });

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      const elapsed = Date.now() - startedAt;
      try {
        const hist = await promediaService.listGrabberHistory(deviceName);
        const mine = hist.filter(h => h.from === g.from);
        const latest = mine[0];
        const latestTs = latest ? `${latest.started}|${latest.finished}` : '';
        // Two signals indicate completion: more rows OR same count but newest
        // row's timestamp pair changed (covers devices that cap history length).
        const completed = mine.length > baselineCount
                       || (latest && latestTs && latestTs !== baselineLatestTs);
        if (completed) {
          clearInterval(pollRef.current); pollRef.current = null;
          const r = (latest.result || '').toLowerCase();
          const finishedOk = r.includes('finished') && /failed files:\s*0/.test(r);
          setProgress({
            state: finishedOk ? 'done' : 'failed',
            startedAt,
            message: latest.result || 'Sync finished'
          });
          return;
        }
      } catch (_) { /* transient fetch error — keep polling */ }
      // Hard cap at 2 minutes — long enough for any reasonable sync of <500MB
      // over LAN, short enough that hung syncs don't keep the spinner forever.
      if (elapsed > 2 * 60 * 1000) {
        clearInterval(pollRef.current); pollRef.current = null;
        setProgress({ state: 'failed', startedAt,
          message: 'Timeout waiting for sync result (2 min). It may still be running on the device.'
        });
      }
    }, 1200); // poll every 1.2s for fast feedback
  };

  const syncNow = async () => {
    if (!current || !current.id) {
      onStatus('❌ No sync configured', false);
      return;
    }
    onStatus('Starting synchronization…', true);

    // STEP 1: capture baseline BEFORE triggering the sync, so a fast-finishing
    // run still flips us from running → done. Track both count AND the newest
    // row's timestamp pair to detect changes even on devices that cap history.
    let baselineCount = 0;
    let baselineLatestTs = '';
    try {
      const baseline = await promediaService.listGrabberHistory(deviceName);
      const mine = baseline.filter(h => h.from === current.from);
      baselineCount = mine.length;
      baselineLatestTs = mine[0] ? `${mine[0].started}|${mine[0].finished}` : '';
    } catch (_) { /* OK to start with 0 — we'll detect anything new */ }

    try {
      // STEP 2: trigger the sync. Fast-fail on HTTP error.
      await promediaService.grabberSyncNow(deviceName, current.id);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
      setProgress({ state: 'failed', startedAt: Date.now(), message: e.message });
      return;
    }
    // STEP 3: start the watch using the baseline captured pre-POST.
    startProgressWatch(current, baselineCount, baselineLatestTs);
    onStatus('✅ Sync triggered — watching device for completion…', false);
  };

  const startEdit = async () => {
    if (!current) {
      // 2026-05-20 — Pre-fill the URL with the device's own canonical
      // media_files_link so admins don't have to type it by hand and get
      // 403 from our WebDAV server when the owner segment is missing.
      // Also default the username to the device's owner — that's the only
      // user who can read this path. Password still has to be entered.
      const prefillUrl   = (device?.media_files_link || '').trim();
      const prefillUser  = (device?.owner_username || '').trim();
      setWd({
        id: '',
        url: prefillUrl,
        username: prefillUser,
        password: '',
        folder: '/',
        clearFolder: true,
      });
      setEditing(true);
      return;
    }
    setBusy(true);
    onStatus(`Loading current sync (#${current.id})…`, true);
    try {
      const fromDevice = await promediaService.getGrabberWebdav(deviceName, current.id);
      // 2026-05-19 — Listing URL is the source of truth for the URL field.
      // The agent's edit-mode page (`/grabber?update=<id>`) mangles
      // WebDAV URLs that have user:pass embedded (the stored
      // `webdav://user:pass@host/path` round-trips badly through
      // GrabberFormPage.processEdit and the form ends up showing a
      // non-https URL, which we then re-save and corrupt further).
      // The listing-page URL is always the canonical form.
      // Username/password are still read from the edit form (those
      // ARE extracted correctly by processEdit into separate fields).
      const merged = {
        id: current.id,
        url: stripWebdavPrefix(current.from) || (fromDevice?.url) || '',
        username: fromDevice?.username || '',
        password: fromDevice?.password || '',
        folder: (fromDevice?.folder) || current.to || '/',
        clearFolder: fromDevice ? !!fromDevice.clearFolder : (current.clear || '').toLowerCase().includes('yes')
      };
      setWd(merged);
      setEditing(true);
      if (!fromDevice || !fromDevice.username) {
        onStatus('⚠ Device did not return saved credentials — please re-enter the password before saving', false);
      } else {
        onStatus('', false);
      }
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setBusy(false); }
  };

  const cancelEdit = () => {
    setEditing(false);
    setWd(null);
  };

  // 2026-05-20 — Single Save handler that commits ALL dirty state in the
  // tab: WebDAV sync row + auto-sync interval. No confirmations.
  // 2026-05-20 — Also mirrors the URL to the DB row's media_files_link so
  // the device card, Edit Player modal, file-manager, and any other view
  // stay in sync with what's actually on the agent.
  const save = async () => {
    if (!wd) return;
    if (!wd.url) {
      onStatus('❌ WebDAV URL is required', false);
      return;
    }
    const isOverwrite = !!wd.id;
    setBusy(true);
    onStatus(isOverwrite ? 'Saving changes…' : 'Creating sync…', true);
    try {
      await promediaService.saveGrabberWebdav(deviceName, wd);
      // Persist the interval too if admin changed it on this Save.
      if (intervalDirty) {
        const n = Number(intervalVal);
        if (!Number.isNaN(n) && n >= 0) {
          try { await promediaService.setGrabberInterval(deviceName, n); } catch (_) {}
        }
      }
      // 2026-05-20 — Mirror the new WebDAV URL onto the device row so the
      // Edit Player modal, file manager, and listing all agree with the
      // sync's actual URL. Fires whenever wd.url is non-empty — we DON'T
      // compare against device.media_files_link because that prop is a
      // snapshot from when the modal opened and can be stale after a
      // rename. Backend deduplicates correctly. Also: omitting brand_id /
      // branch_id from the body lets the server keep its current values
      // AND auto-resolve branch_id from the URL via
      // autoSyncBranchFromDevice (so admin changing the URL's branch
      // segment automatically reassigns the device to that branch).
      if (device?.device_uid && wd.url) {
        try {
          const token = localStorage.getItem('token');
          const r = await fetch(`/api/devices/${encodeURIComponent(device.device_uid)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            credentials: 'include',
            body: JSON.stringify({
              device_name:      device.device_name,
              description:      device.description ?? '',
              register_date:    device.register_date ?? null,
              anydesk_id:       device.anydesk_id ?? '',
              anydesk_password: device.anydesk_password ?? '',
              media_files_link: wd.url,
              // brand_id / branch_id intentionally omitted — the backend
              // will re-derive branch_id from the URL.
            }),
          });
          if (!r.ok) {
            const body = await r.json().catch(() => ({}));
            console.warn('[FileSync save] DB mirror failed:', r.status, body);
          } else {
            console.log('[FileSync save] DB mirror succeeded for', device.device_uid);
          }
        } catch (e) { console.warn('mirror media_files_link failed:', e?.message); }
      }
      onStatus(isOverwrite ? '✅ Sync saved' : '✅ Sync created', false);
      try { onDeviceChanged?.({ device_uid: device?.device_uid, media_files_link: wd.url }); } catch (_) {}
      setEditing(false);
      setWd(null);
      setProgress(null);
      await refresh();
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!current) return;
    setBusy(true);
    onStatus('Deleting sync…', true);
    try {
      await promediaService.grabberDelete(deviceName, current.id);
      onStatus('✅ Sync removed', false);
      setEditing(false); setWd(null); setProgress(null);
      await refresh();
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setBusy(false); }
  };

  const intLabel = (intervalVal === '0' || !intervalVal) ? 'OFF' : `every ${intervalVal} s`;

  // 2026-05-19 — Dirty calculation:
  //   • New sync (no id): any URL typed enables Save.
  //   • Existing sync: any field differs from wdBaseline. Baseline is set
  //     on load + when lazy-loaded credentials arrive, so the form doesn't
  //     flag dirty the moment the username gets populated.
  const wdDirty = !!wd && (
    !wd.id
      ? !!(wd.url || '').trim()
      : !!wdBaseline && (
          wd.url           !== wdBaseline.url ||
          wd.folder        !== wdBaseline.folder ||
          !!wd.clearFolder !== !!wdBaseline.clearFolder ||
          wd.username      !== wdBaseline.username ||
          wd.password      !== wdBaseline.password
        )
  );

  return (
    <div className="dqm-tab dqm-tab--compact">
      {/* 2026-05-20 — Render body in a sub-component so the URL split
          (base / player) and localStorage helpers live with the layout. */}
      <FileSyncBody
        device={device}
        deviceName={deviceName}
        editName={editName}
        setEditName={setEditName}
        savingName={savingName}
        nameDirty={nameDirty}
        saveDisplayName={saveDisplayName}
        editToken={editToken}
        setEditToken={setEditToken}
        savingToken={savingToken}
        tokenDirty={tokenDirty}
        saveMqttToken={saveMqttToken}
        current={current}
        loading={loading}
        wd={wd}
        setWd={setWd}
        wdDirty={wdDirty}
        busy={busy}
        progress={progress}
        extraCount={extraCount}
        intervalVal={intervalVal}
        setIntervalVal={setIntervalVal}
        intervalDirty={intervalDirty}
        setIntervalDirty={setIntervalDirty}
        startEdit={startEdit}
        cancelEdit={cancelEdit}
        save={save}
        syncNow={syncNow}
        del={del}
      />
    </div>
  );
}

// 2026-05-20 — Split out body so the URL state-derivation hooks (baseUrl /
// playerPath / localStorage default) live in their own component.
function FileSyncBody({
  device, deviceName,
  editName, setEditName, savingName, nameDirty, saveDisplayName,
  editToken, setEditToken, savingToken, tokenDirty, saveMqttToken,
  current, loading, wd, setWd, wdDirty, busy, progress, extraCount,
  intervalVal, setIntervalVal, intervalDirty, setIntervalDirty,
  startEdit, cancelEdit, save, syncNow, del,
}) {
  // Default base URL: localStorage-remembered so admin doesn't re-type for
  // each device on the same WebDAV server. Falls back to a sensible default
  // matching this deployment's mqtt.promedia.ltd → WebDAV proxy.
  const DEFAULT_BASE = 'https://mqtt.promedia.ltd/home/';
  const initialBase = (() => {
    try { return localStorage.getItem('pm_webdav_base') || DEFAULT_BASE; }
    catch { return DEFAULT_BASE; }
  })();

  // Derive base+player from wd.url. If wd.url contains "/home/", split there;
  // otherwise leave base as the saved/default and put the entire URL into the
  // player path (admin can fix).
  const splitUrl = (url) => {
    if (!url) return { base: initialBase, player: '' };
    const idx = url.indexOf('/home/');
    if (idx < 0) return { base: initialBase, player: url };
    const base = url.substring(0, idx + '/home/'.length);
    const player = url.substring(idx + '/home/'.length);
    return { base, player };
  };

  const split = splitUrl(wd?.url);

  const onBaseChange = (newBase) => {
    let b = newBase || '';
    if (!b.endsWith('/')) b = b + '/';
    try { localStorage.setItem('pm_webdav_base', b); } catch {}
    if (!wd) return;
    setWd({ ...wd, url: b + split.player });
  };
  const onPlayerChange = (newPlayer) => {
    const p = (newPlayer || '').replace(/^\/+/, '');
    if (!wd) return;
    setWd({ ...wd, url: split.base + p });
  };

  return (
    <>
      {/* Identity row: NAME | MQTT (auto-sync moved to the sync row below). */}
      <section className="dqm-id-row3 dqm-id-row3--two">
        <div className="dqm-cell">
          <label>NAME</label>
          <div className="dqm-cell__row">
            <input type="text" className="dqm-input dqm-input--xs"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              disabled={savingName}
              placeholder={device?.device_name || ''} />
            <button className={`dqm-btn ${nameDirty ? 'primary' : 'ghost'} xs`}
              onClick={saveDisplayName} disabled={savingName || !nameDirty}
              title="Push the new display name to the device and reload">
              {savingName ? '…' : '💾'}
            </button>
          </div>
        </div>
        <div className="dqm-cell">
          <label>MQTT</label>
          <div className="dqm-cell__row">
            <input type="text" className="dqm-input dqm-input--xs dqm-band__mono"
              value={editToken}
              onChange={(e) => setEditToken(e.target.value)}
              disabled={savingToken}
              placeholder={device?.device_uid || ''} />
            <button className={`dqm-btn ${tokenDirty ? 'warn' : 'ghost'} xs`}
              onClick={saveMqttToken} disabled={savingToken || !tokenDirty}
              title="Change DEVICE_TOKEN: device reloads onto the new topic; frees the old UID for a new device">
              {savingToken ? '…' : '🔁'}
            </button>
          </div>
        </div>
      </section>

      {!current && !loading && !wd && (
        <div className="dqm-empty-card dqm-empty-card--slim">
          <span>No synchronization configured.</span>
          <button className="dqm-btn primary small" onClick={startEdit} disabled={busy}>+ Add WebDAV sync</button>
        </div>
      )}

      {wd && (
        <section className="dqm-sync-edit">
          {/* Header: badge + status + Sync now + Delete */}
          <header className="dqm-sync-edit__head">
            <span className="dqm-sync-edit__badge">WEBDAV</span>
            <span
              className="dqm-sync-edit__status"
              title={progress?.message || ''}
            >
              {renderProgressCell(progress) || <span className="dqm-hint">{wd.id ? 'Idle' : 'New (unsaved)'}</span>}
            </span>
            {extraCount > 0 && (
              <span className="dqm-hint">+{extraCount} hidden</span>
            )}
            <div className="dqm-sync-edit__head-actions">
              {wd.id && (
                <button className="dqm-btn primary xs" onClick={syncNow} disabled={busy || wdDirty}
                  title={wdDirty ? 'Save your changes first' : 'Trigger sync now'}>▶ Sync now</button>
              )}
              {wd.id && (
                <button className="dqm-btn danger xs" onClick={del} disabled={busy} title="Delete sync">🗑</button>
              )}
            </div>
          </header>

          {/* Row 1: Base | Player | User | Pass */}
          <div className="dqm-sync-edit__row4">
            <div className="dqm-cell dqm-cell--base">
              <label>Base</label>
              <input type="text" className="dqm-input dqm-input--xs dqm-band__mono"
                value={split.base}
                onChange={(e) => onBaseChange(e.target.value)}
                disabled={busy}
                placeholder={DEFAULT_BASE} />
            </div>
            <div className="dqm-cell dqm-cell--player">
              <label>Player path</label>
              <input type="text" className="dqm-input dqm-input--xs dqm-band__mono"
                value={split.player}
                onChange={(e) => onPlayerChange(e.target.value)}
                disabled={busy}
                placeholder="branch/device" />
            </div>
            <div className="dqm-cell">
              <label>User</label>
              <input type="text" className="dqm-input dqm-input--xs" autoComplete="off"
                value={wd.username} onChange={(e) => setWd({ ...wd, username: e.target.value })} disabled={busy} />
            </div>
            <div className="dqm-cell">
              <label>Pass {wd.id && !wd.password && <span className="dqm-hint" style={{ marginLeft: 4 }}>(blank=keep)</span>}</label>
              <input type="password" className="dqm-input dqm-input--xs" autoComplete="off"
                value={wd.password} onChange={(e) => setWd({ ...wd, password: e.target.value })} disabled={busy}
                placeholder={wd.id ? '•••' : ''} />
            </div>
          </div>

          {/* Row 2: Interval (no own save) | Folder | Clear | Save changes */}
          <div className="dqm-sync-edit__row4 dqm-sync-edit__row4--bottom">
            <div className="dqm-cell">
              <label>Auto-sync (s)</label>
              <input type="number" min="0" className="dqm-input dqm-input--xs"
                value={intervalVal}
                onChange={(e) => { setIntervalVal(e.target.value); setIntervalDirty(true); }}
                disabled={busy}
                placeholder="0 = off" />
            </div>
            <div className="dqm-cell">
              <label>Folder</label>
              <input type="text" className="dqm-input dqm-input--xs"
                value={wd.folder} onChange={(e) => setWd({ ...wd, folder: e.target.value })} disabled={busy} />
            </div>
            <div className="dqm-cell dqm-cell--check">
              <label>&nbsp;</label>
              <label className="dqm-inline-check">
                <input type="checkbox" checked={!!wd.clearFolder}
                  onChange={(e) => setWd({ ...wd, clearFolder: e.target.checked })} disabled={busy} />
                <span>Clear folder</span>
              </label>
            </div>
            <div className="dqm-cell dqm-cell--savebtn">
              <label>&nbsp;</label>
              <button className="dqm-btn primary small dqm-sync-edit__savebtn" onClick={save}
                disabled={busy || (!!current && !wdDirty && !intervalDirty)}>
                {busy ? 'Working…' : (wd.id ? '💾 Save changes' : '💾 Create sync')}
              </button>
            </div>
          </div>

          {!wd.id && (
            <div className="dqm-sync-edit__footer">
              <button className="dqm-btn ghost small" onClick={cancelEdit} disabled={busy}>Cancel</button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

// Helper used by FileSyncTab: pull the URL out of the listing's "From" cell.
// The agent renders it as "WebDAV - https://..." — we strip the prefix so we can
// pre-fill the URL input if the device's edit-page (?update=) returns nothing.
function stripWebdavPrefix(s) {
  if (!s) return '';
  return s.replace(/^\s*(?:WebDAV|SFTP|FTP|Google Drive|Dropbox|HTTP|HTTPS)\s*-\s*/i, '').trim();
}

// Renders the progress badge cell. Defined outside FileSyncTab so we can reuse
// it for the single current row without recreating the closure on every render.
function renderProgressCell(p) {
  if (!p) return null;
  if (p.state === 'running') {
    const elapsed = Math.round((Date.now() - p.startedAt) / 1000);
    return <div className="dqm-sync-prog running"><span className="dqm-spinner" /> Syncing… {elapsed}s</div>;
  }
  if (p.state === 'done') {
    return <div className="dqm-sync-prog done" title={p.message}>✅ {p.message.slice(0, 120)}{p.message.length > 120 ? '…' : ''}</div>;
  }
  return <div className="dqm-sync-prog failed" title={p.message}>❌ {p.message.slice(0, 120)}{p.message.length > 120 ? '…' : ''}</div>;
}

// ============================================================
// RSS tab — list current messages + add new
// ============================================================
function RssTab({ deviceName, onStatus }) {
  const [messages, setMessages] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const titleRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    onStatus('Loading RSS messages…', true);
    try {
      const list = await promediaService.listRssMessages(deviceName);
      setMessages(list);
      onStatus(`Loaded ${list.length} message(s)`, false);
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setLoading(false); }
  }, [deviceName, onStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = async () => {
    if (!title.trim() && !content.trim()) {
      onStatus('❌ Title or content is required', false);
      return;
    }
    setSaving(true);
    onStatus('Adding RSS message…', true);
    try {
      await promediaService.addRssMessage(deviceName, title.trim(), content.trim());
      onStatus('✅ RSS message added', false);
      setTitle(''); setContent('');
      await refresh();
      titleRef.current?.focus();
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setSaving(false); }
  };

  const startEdit = async (m) => {
    setEditingId(m.id);
    setEditTitle(''); setEditContent('');
    setBusy(true);
    onStatus(`Loading message #${m.id}…`, true);
    try {
      const full = await promediaService.getRssMessage(deviceName, m.id);
      setEditTitle(full.title || '');
      setEditContent(full.content || '');
      onStatus('', false);
    } catch (e) {
      // Fallback to the values we already saw in the listing
      setEditTitle(m.title || '');
      setEditContent(m.content || '');
      onStatus(`(using cached values: ${e.message})`, false);
    } finally { setBusy(false); }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle(''); setEditContent('');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setBusy(true);
    onStatus(`Updating message #${editingId}…`, true);
    try {
      await promediaService.updateRssMessage(deviceName, editingId, editTitle.trim(), editContent.trim());
      onStatus('✅ Updated', false);
      cancelEdit();
      await refresh();
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setBusy(false); }
  };

  const del = async (m) => {
    const ok = window.confirm(`Delete RSS message #${m.id}?\n\n${m.title || '(no title)'}\n${m.content}`);
    if (!ok) return;
    setBusy(true);
    onStatus('Deleting…', true);
    try {
      await promediaService.deleteRssMessage(deviceName, m.id);
      onStatus('✅ Deleted', false);
      if (editingId === m.id) cancelEdit();
      await refresh();
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setBusy(false); }
  };

  return (
    <div className="dqm-tab">
      <div className="dqm-info-banner">
        💡 RSS messages defined here are shown on screen when the RSS content's URL
        is set to <code>local</code>. Set up via the on-screen content item.
      </div>

      <h4 className="dqm-h4">{editingId ? `Edit message #${editingId}` : 'Add new message'}</h4>
      <div className="dqm-grid-2">
        <div className="dqm-field" style={{ gridColumn: '1 / -1' }}>
          <label>Title</label>
          <input
            ref={titleRef}
            type="text" className="dqm-input"
            value={editingId ? editTitle : title}
            onChange={(e) => editingId ? setEditTitle(e.target.value) : setTitle(e.target.value)}
            disabled={saving || busy}
            maxLength={105}
            placeholder="(optional)"
          />
        </div>
        <div className="dqm-field" style={{ gridColumn: '1 / -1' }}>
          <label>Content</label>
          <input
            type="text" className="dqm-input"
            value={editingId ? editContent : content}
            onChange={(e) => editingId ? setEditContent(e.target.value) : setContent(e.target.value)}
            disabled={saving || busy}
            maxLength={215}
            placeholder="Message text shown on the screen"
          />
        </div>
      </div>
      <div className="dqm-actions">
        {editingId ? (
          <>
            <button className="dqm-btn primary" onClick={saveEdit} disabled={busy}>
              {busy ? 'Saving…' : '💾 Save changes'}
            </button>
            <button className="dqm-btn ghost" onClick={cancelEdit} disabled={busy}>Cancel</button>
          </>
        ) : (
          <>
            <button className="dqm-btn primary" onClick={add} disabled={saving}>
              {saving ? 'Adding…' : '+ Add message'}
            </button>
            <button className="dqm-btn ghost" onClick={refresh} disabled={saving || loading || busy}>↻ Refresh</button>
          </>
        )}
      </div>

      <h4 className="dqm-h4" style={{ marginTop: 16 }}>Current messages</h4>
      <table className="dqm-table">
        <thead>
          <tr>
            <th style={{ width: 200 }}>Title</th>
            <th>Content</th>
            <th style={{ width: 180 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {messages.length === 0 && !loading && (
            <tr><td colSpan="3" className="empty">No RSS messages yet</td></tr>
          )}
          {messages.map((m, i) => (
            <tr key={i} className={editingId === m.id ? 'active' : ''}>
              <td data-label="Title"><strong>{m.title || <span className="dqm-hint">(no title)</span>}</strong></td>
              <td data-label="Content" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{m.content}</td>
              <td data-label="Actions">
                <button className="dqm-btn ghost small" onClick={() => startEdit(m)} disabled={busy || saving}>
                  ✎ Edit
                </button>
                <button className="dqm-btn danger small" onClick={() => del(m)} disabled={busy || saving} style={{ marginLeft: 6 }}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// License tab — mirrors the agent's own "About software" page
// (device ID + license key + USB Auto-Import status). Admin only.
// Saving posts the key exactly as the agent's form does, then reloads the
// app so the watermark clears, then re-reads the page to confirm.
// ============================================================
function LicenseTab({ deviceName, onStatus }) {
  const [info, setInfo] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Set once the agent accepts a key. We do NOT re-read the page to confirm:
  // the player is restarting at that moment and would only time out.
  const [justActivated, setJustActivated] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    onStatus('Reading license info…', true);
    try {
      const data = await promediaService.getLicenseInfo(deviceName);
      setInfo(data);
      // Deliberately NOT loaded into the box. Once a player is activated the
      // key has done its job and is of no further use here (owner, 2026-11-04);
      // the box stays empty and ready for a replacement key.
      setKeyInput('');
      // The agent returns the key box EMPTY even on an activated player
      // (verified on gedo-01 after a successful activation), so its current
      // licence state simply is not readable from here. Say nothing about it
      // rather than claim "not activated" on a player that is.
      onStatus('Ready — paste a license key to activate this player', false);
    } catch (e) {
      setInfo(null);
      onStatus(`❌ ${e.message}`, false);
    } finally { setLoading(false); }
  }, [deviceName, onStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    const key = (keyInput || '').trim();
    if (!key) { onStatus('❌ Enter a license key first', false); return; }
    setSaving(true);
    onStatus('Saving license key…', true);
    try {
      const verdict = await promediaService.saveLicenseKey(deviceName, key);

      // The player judges the key itself. A rejected key must NOT clear the
      // box, must NOT claim activation and must NOT reload: leave the key in
      // place so it can be corrected, and show the player's own wording.
      if (verdict.rejected) {
        onStatus(`❌ ${verdict.message}`, false);
        return;
      }

      // Accepted. Reload so the activation takes effect and the watermark
      // clears. NOT awaited on purpose: the player restarts and never answers
      // this command, so awaiting it just burns the 30s command timeout.
      try {
        promediaService.setDevice(deviceName);
        promediaService.reloadApp().catch(() => {});
      } catch (_) { /* best-effort */ }

      // The key has done its job — clear it and stop. No confirmation re-read:
      // the player is restarting and that request would only time out.
      setKeyInput('');
      setJustActivated(true);
      onStatus(
        verdict.message
          ? `✅ ${verdict.message} — player reloading`
          : '✅ Key accepted — player reloading, watermark removed',
        false
      );
    } catch (e) {
      onStatus(`❌ ${e.message}`, false);
    } finally { setSaving(false); }
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(info?.deviceId || '');
      onStatus('Device ID copied', false);
    } catch (_) { onStatus('❌ Could not copy', false); }
  };

  if (loading && !info) return <div className="dqm-loading">Reading license info…</div>;

  if (!info) {
    return (
      <div className="dqm-empty-card dqm-empty-card--slim">
        <span>Could not read the license page from this player.</span>
        <button className="dqm-btn primary small" onClick={refresh}>Retry</button>
      </div>
    );
  }

  // Only what we actually witnessed this session counts as "activated".
  const licensed = justActivated;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <section className="dqm-cell">
        <label>DEVICE ID</label>
        <div className="dqm-cell__row">
          <input type="text" className="dqm-input dqm-band__mono" value={info.deviceId || '(not reported)'} readOnly />
          <button className="dqm-btn ghost xs" onClick={copyId} disabled={!info.deviceId} title="Copy device ID">📋</button>
        </div>
        <div className="dqm-hint">Give this ID to support to obtain a license key.</div>
      </section>

      <section className="dqm-cell">
        <label>LICENSE KEY</label>
        <div className="dqm-cell__row">
          <input type="text" className="dqm-input" value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder={licensed ? 'Paste a new key only if you need to replace it' : 'Paste the license key here'}
            disabled={saving || !info.hasForm} />
          <button className={`dqm-btn ${keyInput.trim() ? 'primary' : 'ghost'} xs`}
            onClick={save}
            disabled={saving || !info.hasForm || !keyInput.trim()}
            title="Save the key and reload the player">
            {saving ? '…' : '💾'}
          </button>
        </div>
        <div className="dqm-hint">
          {licensed
            ? '✅ Key accepted by this player — it is reloading and the watermark will be gone. The key is not needed here any more.'
            : 'The player does not report whether it is already licensed. Paste a key and save: the player checks it and rejects a wrong one.'}
          {!info.hasForm && ' This player\'s app build does not expose the license form.'}
        </div>
      </section>

      <section className="dqm-cell">
        <label>USB AUTO-IMPORT</label>
        <div style={{
          padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
          background: info.usbGranted ? '#ECFDF5' : '#FEF3C7',
          border: `1px solid ${info.usbGranted ? '#6EE7B7' : '#FCD34D'}`,
          color: info.usbGranted ? '#065F46' : '#92400E',
        }}>
          {info.usbStatus || 'Not reported by this player'}
        </div>
        <div className="dqm-hint">USB drives with a 'slideshow' folder auto-import their files.</div>
      </section>

      <div>
        <button className="dqm-btn ghost small" onClick={refresh} disabled={loading || saving}>↻ Refresh</button>
      </div>
    </div>
  );
}

// ============================================================
// Main modal
// ============================================================
export default function DeviceQuickManageModal({ device, onClose, isAdmin = false, onDeviceChanged }) {
  // 2026-05-19 — File Sync is admin-only. Builds the visible tab list once
  // based on isAdmin; if a non-admin lands on 'sync' (stale state), fall
  // back to the first visible tab.
  const ADMIN_ONLY_TABS = ['sync', 'license'];
  const visibleTabs = isAdmin ? TABS : TABS.filter(t => !ADMIN_ONLY_TABS.includes(t.id));
  const [tab, setTab] = useState('layouts');
  useEffect(() => {
    if (!visibleTabs.find(t => t.id === tab)) setTab(visibleTabs[0]?.id || 'layouts');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  // 2026-05-19 Phase C — the mqtt-http-bridge keys all device proxy paths
  // (/api/device/:deviceName/...) on the agent's MQTT topic identifier,
  // which is device_uid. The friendly device_name (admin-editable) would
  // route to the wrong topic and time out. Fall back to device_name for
  // legacy rows where device_uid isn't populated.
  const deviceName = device?.device_uid || device?.device_name;

  const onStatus = useCallback((msg, isBusy) => {
    setStatus(msg);
    setBusy(!!isBusy);
  }, []);

  useEffect(() => {
    (async () => {
      if (!deviceName) return;
      await promediaService.ensureBridgeAuth(deviceName);
      setReady(true);
    })();
  }, [deviceName]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!deviceName) return null;

  return (
    <div className="dqm-overlay" onClick={onClose}>
      <div className="dqm-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dqm-header">
          <div>
            <div className="dqm-title">📋 Player Manager</div>
            {/* 2026-05-19 — show the friendly display name under the title.
                The MQTT UID is kept as `deviceName` for routing internally,
                but admins want the human-readable label here. */}
            <div className="dqm-subtitle">{device?.device_name || deviceName}</div>
          </div>
          <button className="dqm-close" onClick={onClose} title="Close (Esc)">×</button>
        </header>

        <nav className="dqm-tabs">
          {visibleTabs.map(t => {
            // Strip leading emoji from full label so we can render text-only
            // caption on mobile (emoji shown via the .short element instead).
            const captionText = t.label.replace(/^[^\w]+\s*/, '');
            return (
              <button
                key={t.id}
                className={`dqm-tab-btn ${tab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
                disabled={!ready}
                title={captionText}
              >
                {/* Desktop: full label shown inline */}
                <span className="dqm-tab-full">{t.label}</span>
                {/* Mobile: emoji on top + small caption below */}
                <span className="dqm-tab-emoji" aria-hidden="true">{t.short}</span>
                <span className="dqm-tab-caption">{captionText}</span>
              </button>
            );
          })}
        </nav>

        <StatusBar status={status} busy={busy} />

        <main className="dqm-body">
          {!ready ? (
            <div className="dqm-loading">Connecting to device bridge…</div>
          ) : (
            <>
              {tab === 'layouts'   && <LayoutsTab   deviceName={deviceName} onStatus={onStatus} />}
              {tab === 'playlists' && <PlaylistsTab deviceName={deviceName} onStatus={onStatus} />}
              {tab === 'schedule'  && <ScheduleTab  deviceName={deviceName} onStatus={onStatus} />}
              {tab === 'sync'      && <FileSyncTab  device={device} deviceName={deviceName} onStatus={onStatus} onClose={onClose} onDeviceChanged={onDeviceChanged} />}
              {tab === 'rss'       && <RssTab       deviceName={deviceName} onStatus={onStatus} />}
              {tab === 'license'   && <LicenseTab   deviceName={deviceName} onStatus={onStatus} />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
